/**
 * The cave being edited: a grid plus its settings, and the conversions to and from a `CaveDef`.
 * Custom caves have one level and the id 'K' (docs/spec/07 §4). Pure.
 */

import type { CaveColors, CaveDef, CaveFill } from '../../caves/types';
import type { Grid } from './grid';
import { blankGrid, gridFromRows, gridToRows, paint } from './grid';

export interface KitDoc {
  readonly grid: Grid;
  readonly name: string;
  readonly hint: string;
  readonly colors: CaveColors;
  readonly diamondValue: number;
  readonly extraValue: number;
  /** Seconds of slow amoeba growth (saved only when the cave has amoeba). */
  readonly amoebaTime: number;
  /** Seconds a magic wall runs (saved only when the cave has one). */
  readonly magicWallTime: number;
  readonly fill: CaveFill;
  /** The one level: random-fill seed, quota, seconds, ms per scan. */
  readonly seed: number;
  readonly diamonds: number;
  readonly time: number;
  readonly tick: number;
}

export const KIT_ID = 'K';
export const DEFAULT_TICK = 150;
export const DEFAULT_AMOEBA_TIME = 30;
export const DEFAULT_MAGIC_TIME = 20;
export const MAX_FILL_ITEMS = 4;

/** A fresh 40 x 22 cave: steel border, dirt, the inbox top left and the exit bottom right. */
export function newDoc(): KitDoc {
  const grid = paint(paint(blankGrid(40, 22), [{ x: 3, y: 2 }], 'P'), [{ x: 36, y: 19 }], 'X');
  return {
    grid,
    name: 'NEW CAVE',
    hint: '',
    colors: [8, 12, 1],
    diamondValue: 10,
    extraValue: 15,
    amoebaTime: DEFAULT_AMOEBA_TIME,
    magicWallTime: DEFAULT_MAGIC_TIME,
    fill: { base: '.', items: [['r', 10], ['d', 4]] },
    seed: 1,
    diamonds: 10,
    time: 150,
    tick: DEFAULT_TICK,
  };
}

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** Any cave (a Classic one, a shared one) as an editable doc: its first level becomes the one level. */
export function docFromDef(def: CaveDef): KitDoc {
  const base = newDoc();
  const lv = def.levels[0];
  const c = def.colors;
  return {
    grid: def.map.length > 0 ? gridFromRows(def.map) : base.grid,
    name: typeof def.name === 'string' ? def.name : base.name,
    hint: typeof def.hint === 'string' ? def.hint : '',
    colors: [num(c[0], 8), num(c[1], 12), num(c[2], 1)],
    diamondValue: num(def.diamondValue, base.diamondValue),
    extraValue: num(def.extraValue, base.extraValue),
    amoebaTime: num(def.amoebaTime, DEFAULT_AMOEBA_TIME),
    magicWallTime: num(def.magicWallTime, DEFAULT_MAGIC_TIME),
    fill: def.fill ? { base: def.fill.base, items: def.fill.items.slice(0, MAX_FILL_ITEMS) } : { base: '.', items: [] },
    seed: num(lv?.seed, 1),
    diamonds: num(lv?.diamonds, base.diamonds),
    time: num(lv?.time, base.time),
    tick: num(lv?.tick, DEFAULT_TICK),
  };
}

/** Whether the map or the random fill uses `ch` (as validation counts it). */
export function uses(doc: KitDoc, ch: string): boolean {
  return doc.grid.cells.includes(ch) || doc.fill.base === ch || doc.fill.items.some(([c]) => c === ch);
}

/** The doc as a playable, saveable, shareable cave. Amoeba and magic wall times only when used. */
export function docToDef(doc: KitDoc): CaveDef {
  return {
    id: KIT_ID,
    name: doc.name,
    hint: doc.hint,
    intermission: false,
    colors: doc.colors,
    diamondValue: doc.diamondValue,
    extraValue: doc.extraValue,
    ...(uses(doc, 'a') ? { amoebaTime: doc.amoebaTime } : {}),
    ...(uses(doc, 'm') ? { magicWallTime: doc.magicWallTime } : {}),
    fill: doc.fill,
    levels: [{ seed: doc.seed, diamonds: doc.diamonds, time: doc.time, tick: doc.tick }],
    map: gridToRows(doc.grid),
  };
}

/** A string that changes whenever the saved cave would: for "unsaved changes". */
export function docSignature(doc: KitDoc): string {
  return JSON.stringify(docToDef(doc));
}
